import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { promotionAPI, partnerTrackAPI } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useNavigate } from "react-router-dom";
import {
  Award,
  Check,
  X,
  ChevronRight,
  Lock,
  Unlock,
  ArrowUpRight,
  Star,
  BookOpen,
  Target,
  Crown,
  AlertCircle,
  Clock,
  Calendar,
  CheckCircle2,
  XCircle,
  History,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_exec-talent-hub-1/artifacts/7a127wcx_Color%20logo%20-%20no%20background.svg";

const LEVEL_COLORS = {
  1: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-200", accent: "#64748b" },
  2: { bg: "bg-sky-50", text: "text-sky-700", border: "border-sky-200", accent: "#0284c7" },
  3: { bg: "bg-[#005d6c]/10", text: "text-[#005d6c]", border: "border-[#005d6c]/20", accent: "#005d6c" },
  4: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", accent: "#d97706" },
  5: { bg: "bg-slate-900", text: "text-white", border: "border-slate-900", accent: "#0f172a" },
};

const READINESS_LABELS = {
  learning: "Learning",
  shadowing: "Shadowing",
  client_ready: "Client-Ready",
  fully_billable: "Fully Billable",
};

const READINESS_COLORS = {
  learning: "bg-slate-100 text-slate-700",
  shadowing: "bg-sky-50 text-sky-700",
  client_ready: "bg-amber-50 text-amber-700",
  fully_billable: "bg-emerald-50 text-emerald-700",
};

const PARTNER_TRACK_COLORS = {
  "not_eligible": { bg: "bg-slate-100", text: "text-slate-600", border: "border-slate-200" },
  "partner_track_entry": { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  "principal_on_partner_track": { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200" },
  "partner_candidate": { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
};

export default function PromotionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [partnerTrack, setPartnerTrack] = useState(null);
  const [criteria, setCriteria] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [promoRes, ptStatusRes, ptCriteriaRes] = await Promise.all([
          promotionAPI.getStatus(user.username),
          partnerTrackAPI.getStatus(user.username),
          partnerTrackAPI.getCriteria()
        ]);
        setStatus(promoRes.data);
        setPartnerTrack(ptStatusRes.data);
        setCriteria(ptCriteriaRes.data);
      } catch (err) {
        console.error("Failed to load status:", err);
      } finally {
        setLoading(false);
      }
    };
    if (user?.username) {
      load();
    }
  }, [user]);

  const formatDate = (isoString) => {
    if (!isoString) return "Never";
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-slate-400 text-sm">Loading progression data...</div>
      </div>
    );
  }

  const levelColors = LEVEL_COLORS[status?.current_level || 1];
  const ptColors = PARTNER_TRACK_COLORS[partnerTrack?.current_stage || "not_eligible"];

  return (
    <div className="space-y-8 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <img src={LOGO_URL} alt="Axentia Global" className="h-10 w-auto" />
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Career Progression</h1>
          <p className="text-sm text-slate-500">Your path to partnership at Axentia Global</p>
        </div>
      </div>

      {/* Current Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Promotion Level Card */}
        <div className={`rounded-sm border-2 ${levelColors.border} ${levelColors.bg} p-6`}>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${status?.current_level === 5 ? 'bg-white/20' : 'bg-white'}`}>
              <Star className={`w-6 h-6 ${levelColors.text}`} />
            </div>
            <div>
              <p className={`text-xs uppercase tracking-wider font-medium ${status?.current_level === 5 ? 'text-white/70' : 'text-slate-500'}`}>
                Promotion Level
              </p>
              <h2 className={`text-xl font-heading font-bold ${levelColors.text}`}>
                L{status?.current_level}: {status?.current_title}
              </h2>
            </div>
          </div>
          <Badge className={READINESS_COLORS[status?.revenue_readiness] || "bg-slate-100"}>
            {READINESS_LABELS[status?.revenue_readiness] || status?.revenue_readiness}
          </Badge>
          <p className={`text-xs mt-3 ${status?.current_level === 5 ? 'text-white/70' : 'text-slate-500'}`}>
            {status?.level_info?.description}
          </p>
        </div>

        {/* Partner Track Card */}
        <div className={`rounded-sm border-2 ${ptColors.border} ${ptColors.bg} p-6`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-sm bg-white flex items-center justify-center">
              <Crown className={`w-6 h-6 ${ptColors.text}`} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider font-medium text-slate-500">
                Partner Track
              </p>
              <h2 className={`text-xl font-heading font-bold ${ptColors.text}`}>
                {partnerTrack?.current_stage_name}
              </h2>
            </div>
          </div>
          {partnerTrack?.validation_due && partnerTrack?.current_stage !== "not_eligible" && (
            <Badge className="bg-amber-100 text-amber-700 border-amber-200">
              <AlertCircle className="w-3 h-3 mr-1" /> Validation Due
            </Badge>
          )}
          {partnerTrack?.last_validation && (
            <p className="text-xs text-slate-500 mt-3">
              Last validated: {formatDate(partnerTrack.last_validation)}
            </p>
          )}
        </div>
      </div>

      {/* Disclaimer Banner */}
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-slate-600 font-medium">Important Notice</p>
          <p className="text-xs text-slate-500 mt-1">
            {criteria?.disclaimer || "Partner Track eligibility does not guarantee partnership. Final decisions are subject to governance review."}
          </p>
        </div>
      </div>

      <Separator />

      {/* Promotion Level Progress Section */}
      <div>
        <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Star className="w-5 h-5 text-[#005d6c]" />
          Promotion Level Requirements
        </h3>

        {/* Level Roadmap */}
        <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4">
          {[1, 2, 3, 4, 5].map((level) => {
            const isCurrentLevel = status?.current_level === level;
            const isCompleted = status?.current_level > level;
            const levelData = status?.all_levels?.[level];
            const lc = LEVEL_COLORS[level];

            return (
              <div key={level} className={`flex items-start gap-4 p-3 rounded-sm ${isCurrentLevel ? lc.bg : ''}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isCompleted ? 'bg-emerald-100 text-emerald-600' : isCurrentLevel ? lc.bg + ' ' + lc.text : 'bg-slate-100 text-slate-400'
                }`}>
                  {isCompleted ? <Check className="w-5 h-5" /> : <span className="text-sm font-bold">L{level}</span>}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold ${isCurrentLevel ? lc.text : isCompleted ? 'text-slate-900' : 'text-slate-400'}`}>
                      {levelData?.title || `Level ${level}`}
                    </span>
                    {isCurrentLevel && <Badge className="bg-[#005d6c] text-white text-[10px]">Current</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{levelData?.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Module Completion for Next Level */}
        {status?.next_level && (
          <div className="mt-4 bg-white border border-slate-200 rounded-sm p-5">
            <h4 className="text-sm font-semibold text-slate-700 mb-3">
              Requirements for L{status.next_level}: {status.next_level_info?.title}
            </h4>
            <div className="mb-3">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-slate-500">Module Completion</span>
                <span className="font-medium">{status.completed_modules}/{status.required_modules_for_next?.length || 0}</span>
              </div>
              <Progress value={(status.completed_modules / (status.required_modules_for_next?.length || 1)) * 100} className="h-2" />
            </div>
            {status.module_gaps?.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-slate-500 mb-2">Incomplete modules:</p>
                <div className="flex flex-wrap gap-1">
                  {status.module_gaps.map((modId) => (
                    <Badge 
                      key={modId} 
                      variant="outline" 
                      className="text-[10px] cursor-pointer hover:bg-slate-100"
                      onClick={() => navigate(`/modules/${modId}`)}
                    >
                      Module {modId}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <Separator />

      {/* Partner Track Section */}
      <div>
        <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Crown className="w-5 h-5 text-amber-600" />
          Partner Track Progression
        </h3>

        {/* Partner Track Stages */}
        <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4">
          {["not_eligible", "partner_track_entry", "principal_on_partner_track", "partner_candidate"].map((stage, idx) => {
            const isCurrentStage = partnerTrack?.current_stage === stage;
            const isCompleted = criteria?.stages?.indexOf(partnerTrack?.current_stage) > criteria?.stages?.indexOf(stage);
            const stageData = criteria?.criteria?.[stage];
            const sc = PARTNER_TRACK_COLORS[stage];

            return (
              <div key={stage} className={`flex items-start gap-4 p-3 rounded-sm ${isCurrentStage ? sc.bg : ''}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isCompleted ? 'bg-emerald-100 text-emerald-600' : isCurrentStage ? 'bg-white ' + sc.text : 'bg-slate-100 text-slate-400'
                }`}>
                  {isCompleted ? <Check className="w-5 h-5" /> : <span className="text-sm font-bold">{idx + 1}</span>}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold ${isCurrentStage ? sc.text : isCompleted ? 'text-slate-900' : 'text-slate-400'}`}>
                      {criteria?.stage_names?.[stage] || stage}
                    </span>
                    {isCurrentStage && <Badge className="bg-amber-500 text-white text-[10px]">Current</Badge>}
                  </div>
                  {stageData && <p className="text-xs text-slate-500 mt-1">{stageData.description}</p>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Next Stage Criteria */}
        {partnerTrack?.next_stage_criteria && (
          <div className="mt-4 bg-white border border-slate-200 rounded-sm p-5">
            <h4 className="text-sm font-semibold text-slate-700 mb-3">
              Requirements for: {partnerTrack.next_stage_name}
            </h4>
            <p className="text-xs text-slate-500 mb-4">{partnerTrack.next_stage_criteria.description}</p>
            
            <div className="space-y-3">
              {Object.entries(partnerTrack.next_stage_criteria.requirements || {}).map(([key, req]) => (
                <div key={key} className="flex items-start gap-3 p-3 bg-slate-50 rounded-sm">
                  <div className="w-6 h-6 rounded-full bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                    <Target className="w-3 h-3 text-slate-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-700">{req.label}</span>
                      <Badge variant="outline" className="text-[10px]">{req.value}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{req.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Current Stage Criteria (if on track) */}
        {partnerTrack?.current_stage_criteria && (
          <div className="mt-4 bg-white border border-slate-200 rounded-sm p-5">
            <h4 className="text-sm font-semibold text-slate-700 mb-3">
              Current Stage Requirements: {partnerTrack.current_stage_name}
            </h4>
            <div className="space-y-2">
              {Object.entries(partnerTrack.current_stage_criteria.requirements || {}).map(([key, req]) => (
                <div key={key} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <span className="text-sm text-slate-600">{req.label}</span>
                  <Badge variant="outline" className="text-xs">{req.value}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Validation History */}
        {partnerTrack?.validation_history?.length > 0 && (
          <div className="mt-4 bg-white border border-slate-200 rounded-sm overflow-hidden">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">Validation History</span>
                <Badge variant="outline" className="text-[10px]">{partnerTrack.validation_history.length}</Badge>
              </div>
              {showHistory ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            
            {showHistory && (
              <div className="border-t border-slate-200 p-4 space-y-3">
                {partnerTrack.validation_history.map((entry, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-sm">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                      entry.action === 'annual_validation' 
                        ? (entry.passed ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600')
                        : (entry.direction === 'progressed' ? 'bg-emerald-100 text-emerald-600' : entry.direction === 'regressed' ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-600')
                    }`}>
                      {entry.action === 'annual_validation' 
                        ? (entry.passed ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />)
                        : (entry.direction === 'progressed' ? <ArrowUpRight className="w-3 h-3" /> : <Check className="w-3 h-3" />)
                      }
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-700">
                          {entry.action === 'annual_validation' 
                            ? `Annual Validation - ${entry.passed ? 'Passed' : 'Failed'}`
                            : `Stage ${entry.direction}: ${criteria?.stage_names?.[entry.from_stage]} → ${criteria?.stage_names?.[entry.to_stage]}`
                          }
                        </span>
                      </div>
                      {entry.notes && <p className="text-xs text-slate-500 mt-1">{entry.notes}</p>}
                      <p className="text-xs text-slate-400 mt-1">{formatDate(entry.timestamp)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
